import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Asterisk, Check, Sparkles, Wrench } from "lucide-react";
import { Reveal } from "../components/common/Reveal";
import { SeoHead } from "../components/common/SeoHead";

const JAKARTA = { fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" };

const BUILD_PACKAGES = [
  {
    id: "starter-landing", name: "Starter Landing Page", setup: "RM1,800", monthly: null, badge: "One-time",
    line: "For small businesses requiring a simple, professional online presence.",
    scope: ["One professionally designed landing page", "Mobile-responsive design", "WhatsApp call-to-action", "Basic enquiry form", "Basic on-page SEO", "Two revision rounds", "Standard handover guidance"],
    note: "Excludes domain, hosting, advanced integrations, extended copywriting, maintenance and additional pages.",
  },
  {
    id: "build-own", name: "Build & Own", setup: "Starting from RM3,000", monthly: null, badge: "One-time · you own it",
    line: "For businesses that want to own and independently manage their website.",
    scope: ["Up to five standard pages", "Responsive custom design", "WhatsApp & enquiry integration", "Basic SEO setup", "Two revision rounds", "GitHub source-code handover*", "Deployment documentation", "14-day bug-correction window"],
    note: "*Where technically supported — third-party licences, infrastructure and proprietary services may not be transferable. Feasibility confirmed in writing before contract. You manage your own hosting and updates.",
  },
  {
    id: "build-care", name: "Build & Care", setup: "Starting from RM3,800", monthly: "RM500/month", badge: "Setup + monthly care", featured: true,
    line: "For businesses wanting a professionally managed website.",
    scope: ["Up to six standard pages", "Responsive website design", "Enquiry & WhatsApp integration", "Basic SEO + analytics setup", "Deployment coordination", "Routine technical checks & backups*", "2 hours of minor updates monthly", "Monthly maintenance summary", "WhatsApp support, business hours"],
    note: "*Where supported. Excludes new pages, major redesign, custom functionality, paid third-party subscriptions and hosting unless quoted.",
  },
  {
    id: "build-care-grow", name: "Build, Care & Grow", setup: "Starting from RM6,800", monthly: "RM1,500/month", badge: "Setup + monthly growth",
    line: "For SMEs requiring ongoing website and content marketing support.",
    scope: ["Up to eight standard pages", "Business diagnostic & strategy onboarding", "Deployment & maintenance coordination", "3 hours of minor updates monthly", "2 SEO blog articles monthly", "4 campaign posters monthly", "SEO monitoring + content calendar", "Monthly performance summary", "Monthly 30-min strategy review"],
    note: "Max two revision rounds per deliverable. Original, fact-checked content — but no guarantee of ranking, traffic, enquiries or sales. Excludes ad spend, ads management, video production, community management and advanced integrations.",
  },
];

const PAYG = [
  ["Minor website update", "From RM250"], ["New website section", "From RM450"], ["Additional standard webpage", "From RM650"],
  ["SEO blog article", "From RM350"], ["Digital campaign poster", "From RM250"], ["Website health check", "From RM350"],
  ["Technical troubleshooting", "From RM350"],
];

const PROCESS = [
  ["01", "Enquiry", "Tell us about your business in a 3-minute form."],
  ["02", "Review", "We review fit and respond personally — no bots."],
  ["03", "Quotation", "A written quotation with clear scope and exclusions."],
  ["04", "Agreement", "One transparent agreement, accepted online."],
  ["05", "Deposit", "50% deposit secures your build slot."],
  ["06", "Build", "We design, write and build — you review twice."],
  ["07", "Balance", "Final 50% before launch. Then we go live."],
  ["08", "Handover", "Training, access and documentation handed over."],
  ["09", "Care", "Optional monthly care keeps everything growing."],
];

export default function StudioPage() {
  return (
    <main style={JAKARTA} className="bg-[#F8F5EF] text-[#241b3a]" data-testid="studio-page">
      <SeoHead
        title="IAGSB Digital Studio — Websites & Growth Systems, Johor Bahru"
        description="Serious About Growth. Playful By Design. Website packages from RM1,800 — build & own or fully managed with monthly care. One partner, from brand to business growth."
        path="/consulting/digital-studio"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#35205F] text-[#F8F5EF]">
        <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-[#F47735]/20 blur-2xl" aria-hidden="true" />
        <div className="absolute bottom-10 left-6 h-24 w-24 rounded-full border-2 border-dashed border-[#17A6A0]/50" aria-hidden="true" />
        <div className="container-site relative py-28 lg:py-36">
          <Reveal>
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47735]">
              <Asterisk className="h-4 w-4" strokeWidth={2.5} /> IAGSB Digital Studio
            </p>
            <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
              Serious About Growth.<br />
              <span className="text-[#F47735]">Playful</span> By Design.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#F8F5EF]/80 sm:text-lg">
              One partner. From brand to business growth. Own your website outright, or let us run it for you month to month — strategy first, scope in writing, no surprise charges.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/consulting/digital-studio/enquire" data-testid="studio-hero-enquire" className="group inline-flex items-center gap-2 rounded-full bg-[#F47735] px-7 py-3.5 text-sm font-bold text-[#241b3a] transition-transform duration-300 hover:scale-[1.03]">
                Request a quotation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </Link>
              <a href="#packages" data-testid="studio-hero-packages" className="inline-flex items-center gap-2 rounded-full border border-[#F8F5EF]/30 px-7 py-3.5 text-sm font-bold text-[#F8F5EF] transition-colors hover:border-[#17A6A0] hover:text-[#17A6A0]">
                Compare packages
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="py-24 lg:py-32" data-testid="studio-packages">
        <div className="container-site">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#17A6A0]">Service packages</p>
            <h2 className="mt-4 max-w-3xl text-3xl font-extrabold tracking-tight text-[#35205F] sm:text-5xl">
              Own it yourself,<br />or let us take care of it.
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#241b3a]/65">
              One-time build fees are separate from optional monthly retainers — you'll always know exactly which is which. Every price confirmed in a written quotation before anything begins.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {BUILD_PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06} className="h-full">
                <div className={`flex h-full flex-col rounded-3xl border p-7 transition-transform duration-300 hover:-translate-y-1.5 ${p.featured ? "border-[#35205F] bg-[#35205F] text-[#F8F5EF] shadow-2xl" : "border-[#35205F]/15 bg-white"}`} data-testid={`studio-package-${p.id}`}>
                  {p.featured && <p className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#F47735] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#241b3a]"><Sparkles className="h-3 w-3" /> Most popular</p>}
                  <p className={`text-[9px] font-bold uppercase tracking-[0.2em] ${p.featured ? "text-[#17A6A0]" : "text-[#17A6A0]"}`}>{p.badge}</p>
                  <h3 className={`mt-2 text-xl font-extrabold ${p.featured ? "text-[#F8F5EF]" : "text-[#35205F]"}`}>{p.name}</h3>
                  <p className="mt-2 text-2xl font-extrabold leading-tight text-[#F47735]">{p.setup}</p>
                  {p.monthly && <p className={`mt-1 text-sm font-bold ${p.featured ? "text-[#17A6A0]" : "text-[#17A6A0]"}`}>+ {p.monthly}</p>}
                  <p className={`mt-3 text-[13px] leading-relaxed ${p.featured ? "text-[#F8F5EF]/75" : "text-[#241b3a]/65"}`}>{p.line}</p>
                  <ul className="mt-5 flex-1 space-y-2">
                    {p.scope.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-[12.5px] leading-snug">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#17A6A0]" strokeWidth={2.5} /> {s}
                      </li>
                    ))}
                  </ul>
                  {p.note && <p className={`mt-4 text-[10.5px] leading-relaxed ${p.featured ? "text-[#F8F5EF]/55" : "text-[#241b3a]/50"}`}>{p.note}</p>}
                  <Link to={`/consulting/digital-studio/enquire?package=${p.id}`} data-testid={`studio-package-cta-${p.id}`} className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[13px] font-bold transition-transform hover:scale-[1.02] ${p.featured ? "bg-[#F47735] text-[#241b3a]" : "bg-[#35205F] text-[#F8F5EF]"}`}>
                    {p.monthly ? "Explore monthly care" : "Request a quotation"} <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 rounded-2xl border-l-4 border-[#F47735] bg-white p-5 text-xs leading-relaxed text-[#241b3a]/65" data-testid="studio-exclusions">
            All prices in Malaysian Ringgit, subject to final written quotation. Domain, hosting, third-party services, ad spend, media production and ongoing support are excluded unless specified in your quotation. Monthly retainers are payable in advance; allowances do not carry forward; no automatic charges — ever.
          </p>
        </div>
      </section>

      {/* Pay-as-you-go */}
      <section className="bg-white py-24 lg:py-28" data-testid="studio-payg">
        <div className="container-site">
          <Reveal>
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47735]"><Wrench className="h-4 w-4" strokeWidth={2} /> Service menu</p>
            <h2 className="mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-[#35205F] sm:text-4xl">
              Need something done? Pay only for what you need.
            </h2>
          </Reveal>
          <div className="mt-12 overflow-hidden rounded-3xl border border-[#35205F]/12">
            {PAYG.map(([label, price], i) => (
              <div key={label} className={`flex items-center justify-between gap-4 px-6 py-5 transition-colors hover:bg-[#F8F5EF] ${i % 2 ? "bg-[#F8F5EF]/50" : "bg-white"}`} data-testid={`payg-row-${i}`}>
                <p className="text-sm font-bold text-[#35205F]">{label}</p>
                <div className="flex items-center gap-5">
                  <p className="whitespace-nowrap text-sm font-extrabold text-[#17A6A0]">{price}</p>
                  <Link to={`/consulting/digital-studio/enquire?package=payg-${["minor-update","new-section","extra-page","seo-article","poster","health-check","troubleshooting"][i]}`} data-testid={`payg-cta-${i}`} className="hidden whitespace-nowrap rounded-full border border-[#35205F]/25 px-4 py-1.5 text-[11px] font-bold text-[#35205F] transition-colors hover:border-[#F47735] hover:text-[#F47735] sm:inline-block">
                    Request this
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] leading-relaxed text-[#241b3a]/55" data-testid="payg-rules">
            Minimum billable request RM250 · All work subject to scope assessment and written quotation · Urgent work carries a 50% surcharge when accepted, subject to availability · Maximum two revision rounds · Payment in full before standalone jobs begin · No guaranteed turnaround until scope, access and schedule are confirmed · Troubleshooting fees cover agreed diagnostic work; resolution is not guaranteed where the cause is outside our control.
          </p>
          <Link to="/consulting/digital-studio/enquire" data-testid="payg-main-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#35205F] px-7 py-3.5 text-sm font-bold text-[#F8F5EF] transition-transform hover:scale-[1.02]">
            Request a website update <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 lg:py-32" data-testid="studio-process">
        <div className="container-site">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47735]">How it works</p>
            <h2 className="mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-[#35205F] sm:text-5xl">No surprises. Nine honest steps.</h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-[#35205F]/10 bg-[#35205F]/10 sm:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map(([n, t, d]) => (
              <div key={n} className="bg-white p-7 transition-colors duration-300 hover:bg-[#F8F5EF]">
                <p className="text-sm font-extrabold text-[#F47735]">{n}</p>
                <p className="mt-2 font-extrabold text-[#35205F]">{t}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#241b3a]/65">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other services — quotation only */}
      <section className="bg-white py-24" data-testid="studio-other-services">
        <div className="container-site">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#17A6A0]">Also by quotation</p>
            <h2 className="mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-[#35205F] sm:text-4xl">Bigger ambitions? Talk to us first.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-[#35205F]/15 bg-[#F8F5EF] p-8" data-testid="studio-astro">
              <p className="font-extrabold text-[#35205F]">Astro Media Advertising</p>
              <p className="mt-2 text-sm leading-relaxed text-[#241b3a]/65">Commission-based at 15%, subject to a verified partner agreement. Scoped and quoted individually — never an instant checkout.</p>
            </div>
            <div className="rounded-3xl border border-[#35205F]/15 bg-[#F8F5EF] p-8" data-testid="studio-training">
              <p className="font-extrabold text-[#35205F]">Corporate Training &amp; Consulting</p>
              <p className="mt-2 text-sm leading-relaxed text-[#241b3a]/65">Custom programmes for teams and organisations, always by written quotation after a proper conversation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-[#35205F] py-24 text-center text-[#F8F5EF]">
        <Asterisk className="absolute left-1/2 top-8 h-10 w-10 -translate-x-1/2 rotate-12 text-[#F47735]" strokeWidth={2} aria-hidden="true" />
        <div className="container-site relative">
          <h2 className="mx-auto max-w-2xl text-3xl font-extrabold tracking-tight sm:text-5xl">Ready when you are.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#F8F5EF]/75">Three minutes of your time. A real human reply, a written quotation, zero pressure.</p>
          <Link to="/consulting/digital-studio/enquire" data-testid="studio-footer-enquire" className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#F47735] px-8 py-4 text-sm font-bold text-[#241b3a] transition-transform hover:scale-[1.03]">
            Discuss your project <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </main>
  );
}
